import { Suspense, useState } from 'react'
import { MdEdit } from 'react-icons/md'
import { useParams, useNavigate } from 'react-router-dom'
import { MainPage } from 'components/templates/MainPage'
import { useSelectCompany } from 'api/company/useSelectCompany'
import { restoreTask } from 'api/task/restoreTask'
import { LoadingOverlay } from 'components/atoms/overlay/LoadingOverlay'
import swal from 'sweetalert2'

const CompanyDetailContent = ({ companyId }: { companyId: string }) => {
  const { data: company, mutate } = useSelectCompany(companyId)
  const navigate = useNavigate()
  const [showDeleted, setShowDeleted] = useState(false)

  const handleRestore = async (taskId: string) => {
    try {
      await restoreTask(taskId)
      await mutate()
      swal.fire({ icon: 'success', text: 'タスクを復活させました。', confirmButtonText: 'OK' })
    } catch {
      swal.fire({ icon: 'error', text: 'タスクの復活に失敗しました。', confirmButtonText: 'OK' })
    }
  }

  if (!company) return null

  const boolLabel = (v: boolean | null | undefined) =>
    v === true ? 'あり' : v === false ? 'なし' : '-'

  const tasks = company?.tasks ?? []
  const visibleTasks = showDeleted ? tasks : tasks.filter((t) => !t.isDeleted)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800">{company.name}</h1>
        <button
          type="button"
          onClick={() => navigate(`/company/${companyId}/edit`)}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-[#01695C]"
        >
          <MdEdit size={20} />
        </button>
      </div>

      {/* Basic Info */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-base font-semibold text-gray-700">基本情報</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div>
            <dt className="text-xs text-gray-500">ホームページ</dt>
            <dd>
              {company.homepage ? (
                <a
                  href={company.homepage}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#01695C] hover:underline break-all"
                >
                  {company.homepage}
                </a>
              ) : '-'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">郵便番号</dt>
            <dd>{company.postCode || '-'}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">住所</dt>
            <dd>{company.address || '-'}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">固定番号</dt>
            <dd>{company.homePhone || '-'}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">設立年月日</dt>
            <dd>{company.establishment ? new Date(company.establishment).toLocaleDateString('ja-JP') : '-'}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">インボイス</dt>
            <dd>{company.invoice || '-'}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">得意分野</dt>
            <dd>{company.specialty || '-'}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">得意業界</dt>
            <dd>{company.specialtyIndustry || '-'}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">派遣免許有無</dt>
            <dd>{boolLabel(company.dispatchLicense)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">派遣免許番号</dt>
            <dd>{company.dispatchLicenseNumber || '-'}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Pマーク</dt>
            <dd>{boolLabel(company.pMark)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">ISMS</dt>
            <dd>{boolLabel(company.isms)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">営業人数</dt>
            <dd>{company.salesValue ?? '-'}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">プロパー数</dt>
            <dd>{company.properValue ?? '-'}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">BP数</dt>
            <dd>{company.bpValue ?? '-'}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">配信アドレス</dt>
            <dd>{company.deliverAddress || '-'}</dd>
          </div>
        </dl>
      </div>

      {/* Tasks */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-700">タスク</h2>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
            <span>削除済みを表示</span>
            <button
              type="button"
              role="switch"
              aria-checked={showDeleted}
              onClick={() => setShowDeleted((prev) => !prev)}
              className={[
                'relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200',
                showDeleted ? 'bg-[#01695C]' : 'bg-gray-300'
              ].join(' ')}
            >
              <span
                className={[
                  'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200',
                  showDeleted ? 'translate-x-4' : 'translate-x-0'
                ].join(' ')}
              />
            </button>
          </label>
        </div>

        {visibleTasks.length === 0 ? (
          <p className="text-sm text-gray-400">タスクはありません。</p>
        ) : (
          <div className="space-y-2">
            {visibleTasks.map((task) => (
              <div
                key={task.id}
                className={[
                  'flex items-center justify-between rounded-lg border px-4 py-3',
                  task.isDeleted
                    ? 'border-red-100 bg-red-50'
                    : 'border-gray-100 bg-gray-50'
                ].join(' ')}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={[
                      'text-sm font-medium',
                      task.isDeleted ? 'text-gray-400 line-through' : 'text-gray-700'
                    ].join(' ')}>
                      {task.title}
                    </p>
                    {task.isDeleted && (
                      <span className="flex-shrink-0 rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-500">
                        削除済み
                      </span>
                    )}
                  </div>
                  {task.description && (
                    <p className="mt-0.5 truncate text-xs text-gray-500">{task.description}</p>
                  )}
                  <p className="mt-0.5 text-xs text-gray-400">担当: {task.creator}</p>
                  {task.dueDate && (
                    <p className={[
                      'mt-0.5 text-xs',
                      !task.isDeleted && new Date(task.dueDate) < new Date(new Date().toDateString())
                        ? 'font-semibold text-red-500'
                        : 'text-gray-400'
                    ].join(' ')}>
                      期日: {new Date(task.dueDate).toLocaleDateString('ja-JP')}
                    </p>
                  )}
                </div>
                {task.isDeleted && (
                  <button
                    type="button"
                    onClick={() => handleRestore(task.id)}
                    className="ml-4 flex-shrink-0 rounded-lg border border-[#01695C] px-3 py-1.5 text-xs font-medium text-[#01695C] hover:bg-[#e8f5f3]"
                  >
                    復活
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export const CompanyDetailPage = (): JSX.Element => {
  const { companyId } = useParams<{ companyId: string }>()

  return (
    <MainPage>
      <Suspense fallback={<LoadingOverlay />}>
        {companyId && <CompanyDetailContent companyId={companyId} />}
      </Suspense>
    </MainPage>
  )
}
